import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Clock, Zap, AlertCircle, ChevronRight, Calendar } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useSubscription } from '@/hooks/useSubscription';
import { useTestClock } from '@/contexts/TestClockContext';
import { format } from 'date-fns';
import { safeLog } from '@/utils/safeLogger';

export function TestClockManager() {
  const { subscription, isLoading: subLoading, refresh: refreshSubscription } = useSubscription();
  const { isTestMode, frozenTime, isEnabled, setIsEnabled, getCurrentTime, updateTestClock, resetTestClock } = useTestClock();
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [customHours, setCustomHours] = useState<string>('');
  const [lastAdvance, setLastAdvance] = useState<{
    old_time: string;
    new_time: string;
    hours: number;
  } | null>(null);

  const handleAdvanceTime = async (hours: number) => {
    try {
      setIsAdvancing(true);
      toast.loading(`Advancing time by ${hours} hour${hours !== 1 ? 's' : ''}...`);

      // Get stripe_customer_id from active subscription
      const { data: { user } } = await supabase.auth.getUser();
      const { data: subscriptionData } = await supabase
        .from('subscriptions')
        .select('stripe_customer_id')
        .eq('user_id', user?.id)
        .eq('status', 'active')
        .single();

      const { data, error } = await supabase.functions.invoke('test-clock-advance', {
        body: { 
          hours_to_advance: hours,
          stripe_customer_id: subscriptionData?.stripe_customer_id
        }
      });

      toast.dismiss();

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      setLastAdvance({
        old_time: data.old_time,
        new_time: data.new_time,
        hours: hours
      });

      // Update Test Clock Context
      if (data.frozen_time) {
        updateTestClock(data.frozen_time);
      }

      toast.success(`Time advanced by ${hours} hour${hours !== 1 ? 's' : ''}!`, {
        description: 'Webhooks will be processed shortly. Refreshing subscription...'
      });

      // Wait a bit for webhooks to process, then refresh
      setTimeout(() => {
        refreshSubscription(true);
      }, 3000);

    } catch (error: any) {
      toast.dismiss();
      console.error('Error advancing time:', error);
      toast.error('Failed to advance time', {
        description: error.message || 'Unknown error'
      });
    } finally {
      setIsAdvancing(false);
    }
  };

  const handleCustomAdvance = () => {
    const hours = parseFloat(customHours);
    if (isNaN(hours) || hours <= 0) {
      toast.error('Please enter a valid number of hours');
      return;
    }
    if (hours > 48) {
      toast.error('Maximum 48 hours (2 days) at a time for daily subscriptions');
      return;
    }
    handleAdvanceTime(hours);
    setCustomHours('');
  };

  const quickButtons = [
    { label: '+1 Hour', hours: 1 },
    { label: '+6 Hours', hours: 6 },
    { label: '+1 Day', hours: 24 },
    { label: '+2 Days (Max)', hours: 48 },
  ];

  if (subLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (!subscription) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Test Clock Manager
          </CardTitle>
          <CardDescription>
            Fast-forward time to test subscription renewals
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              No active test subscription found. Please create a test subscription first in the Pricing tab.
              <br /><br />
              <strong>Note:</strong> Only subscriptions created in Test Mode will have a Test Clock.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Note: isTestMode comes from context now
  
  return (
    <div className="space-y-6">
      <Card className="app-tile app-tile-hover">
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="app-icon-box">
              <Clock className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="app-section-title">Test Clock Manager</CardTitle>
              <CardDescription>
                Fast-forward time to test subscription renewals and credit resets
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Global Test Clock Toggle */}
          {frozenTime && (
            <div className="app-info-box flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-sm font-semibold mb-1">Test Clock Mode</h3>
                <p className="text-xs text-muted-foreground">
                  {isEnabled 
                    ? "Using simulated time from Stripe Test Clock" 
                    : "Using real-world time (Test Clock paused)"}
                </p>
              </div>
              <Switch
                checked={isEnabled}
                onCheckedChange={setIsEnabled}
              />
            </div>
          )}

          {/* Current Subscription Info */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium">Active Subscription</h3>
            <div className="grid gap-3 p-4 rounded-lg border bg-muted/50">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Tier</span>
                <Badge variant="secondary" className="gap-1">
                  <Zap className="h-3 w-3" />
                  {subscription.tier}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Credits</span>
                <span className="text-sm font-medium">
                  {subscription.credits_remaining} / {subscription.credits_monthly}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Billing Cycle</span>
                <span className="text-sm font-medium capitalize">{subscription.billing_cycle}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Current Period</span>
                <span className="text-sm font-medium">
                  {subscription.current_period_start && format(new Date(subscription.current_period_start), 'MMM d')} → {subscription.current_period_end && format(new Date(subscription.current_period_end), 'MMM d, yyyy')}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Status</span>
                <Badge variant={subscription.status === 'active' ? 'default' : 'secondary'}>
                  {subscription.status}
                </Badge>
              </div>
              {frozenTime && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    {isEnabled ? "Current Simulated Time" : "Current Real Time"}
                  </span>
                  <span className="text-sm font-medium">{format(getCurrentTime(), 'PPP HH:mm')}</span>
                </div>
              )}
              {frozenTime && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Mode</span>
                  <Badge variant={isTestMode ? "default" : "secondary"} className="gap-1">
                    <Clock className="h-3 w-3" />
                    {isTestMode ? "Test Time" : "Real Time"}
                  </Badge>
                </div>
              )}
            </div>
          </div>

          {subscription.billing_cycle === 'daily' && (
            <Alert className="border-amber-500/50 bg-amber-500/10">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription>
                <strong>Daily Subscription Limit:</strong> You can advance time by a maximum of 48 hours (2 days) at once.
                For larger time jumps, advance multiple times in sequence.
              </AlertDescription>
            </Alert>
          )}

          {!isTestMode && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                This subscription is in <strong>Live Mode</strong>. Test Clocks only work with Test Mode subscriptions.
                Please create a new test subscription to use this feature.
              </AlertDescription>
            </Alert>
          )}

          {/* Quick Actions */}
          {isTestMode && (
            <>
              <div className="space-y-3">
                <h3 className="text-sm font-medium">Quick Time Advance</h3>
                <div className="grid grid-cols-2 gap-2">
                  {quickButtons.map(({ label, hours }) => (
                    <Button
                      key={hours}
                      onClick={() => handleAdvanceTime(hours)}
                      disabled={isAdvancing}
                      variant="outline"
                      className="gap-2"
                    >
                      {isAdvancing ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Clock className="h-4 w-4" />
                      )}
                      {label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Custom Advance */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium">Custom Time Advance</h3>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Hours (max 48)"
                    value={customHours}
                    onChange={(e) => setCustomHours(e.target.value)}
                    min="0.1"
                    max="168"
                    step="0.5"
                    disabled={isAdvancing}
                  />
                  <Button
                    onClick={handleCustomAdvance}
                    disabled={isAdvancing || !customHours}
                    className="gap-2"
                  >
                    {isAdvancing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    Advance
                  </Button>
                </div>
              </div>

              {/* Test Clock Status & Reset */}
              {frozenTime && (
                <div className="space-y-3 pt-4 border-t">
                  <h3 className="text-sm font-medium">Test Clock Control</h3>
                  <Alert className={isEnabled 
                    ? "border-blue-500/50 bg-blue-500/10" 
                    : "border-orange-500/50 bg-orange-500/10"
                  }>
                    <Clock className={`h-4 w-4 ${isEnabled ? "text-blue-600" : "text-orange-600"}`} />
                    <AlertDescription>
                      <strong>{isEnabled ? "Test Mode Active" : "Test Clock Paused"}:</strong>
                      {isEnabled 
                        ? " All subscription renewals are simulated using the Stripe Test Clock. Use the controls above to advance time and trigger renewal webhooks."
                        : " Using real-world time. Toggle Test Clock Mode above to resume simulation."}
                      <br />
                      <span className="text-xs text-muted-foreground mt-1 block">
                        {isEnabled 
                          ? `Simulated time: ${format(getCurrentTime(), 'PPP HH:mm')} • Webhooks process asynchronously.`
                          : `Real time: ${format(new Date(), 'PPP HH:mm')} • Test Clock exists but is not active.`}
                      </span>
                    </AlertDescription>
                  </Alert>
                  <Button
                    onClick={resetTestClock}
                    variant="outline"
                    className="w-full gap-2 border-orange-500 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950"
                  >
                    <AlertCircle className="h-4 w-4" />
                    Reset to Real Time (Delete Test Clock)
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Last Advance Info */}
          {lastAdvance && (
            <Alert>
              <Calendar className="h-4 w-4" />
              <AlertDescription>
                <strong>Last Advance:</strong> Moved {lastAdvance.hours} hour{lastAdvance.hours !== 1 ? 's' : ''} forward
                <br />
                <span className="text-xs text-muted-foreground">
                  {format(new Date(lastAdvance.old_time), 'MMM d, yyyy HH:mm')} → {format(new Date(lastAdvance.new_time), 'MMM d, yyyy HH:mm')}
                </span>
              </AlertDescription>
            </Alert>
          )}

          {/* Info */}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs">
              <strong>How it works:</strong> Test Clocks simulate the passage of time in Stripe's Test Mode.
              When you advance time, Stripe triggers real webhooks (invoice.finalized, subscription.updated, etc.)
              and your subscription will renew just like in production. Perfect for testing credit resets and renewals.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
}
